import { useEffect, useState, useCallback } from "react";
import { ownerApi } from "../api";
import { formatCT } from "../lib/time";
import { Loader2, RefreshCw } from "lucide-react";

const EVENT_OPTIONS = [
  "", "owner_login", "owner_login_failed", "owner_logout", "owner_password_changed",
  "owner_created", "owner_deleted", "owner_password_reset",
  "org_created", "org_updated", "org_suspended", "org_archived", "org_deleted", "org_tier_changed",
  "platform_settings_updated",
];

function shortenUA(ua) {
  // Best-effort compact label: "Chrome / macOS", "Firefox / iOS", "curl/7.88.1", etc.
  if (!ua) return "";
  if (/^curl|^python|^node|^PostmanRuntime|^axios/i.test(ua)) return ua.split(" ")[0];
  const browser =
    /Edg\//.test(ua) ? "Edge" :
    /Chrome\//.test(ua) ? "Chrome" :
    /Firefox\//.test(ua) ? "Firefox" :
    /Safari\//.test(ua) ? "Safari" :
    "Browser";
  const os =
    /iPhone|iPad|iOS/.test(ua) ? "iOS" :
    /Mac OS X|Macintosh/.test(ua) ? "macOS" :
    /Android/.test(ua) ? "Android" :
    /Windows/.test(ua) ? "Windows" :
    /Linux/.test(ua) ? "Linux" :
    "unknown";
  return `${browser} · ${os}`;
}

export default function AuditLog() {
  const [events, setEvents] = useState(null);
  const [filter, setFilter] = useState("");

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filter) params.set("event_type", filter);
      params.set("limit", "100");
      const { data } = await ownerApi.get(`/audit-log?${params.toString()}`);
      setEvents(data);
    } catch { setEvents([]); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6" data-testid="owner-audit-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-heading font-bold text-[#f1fcff]">Audit Log</h1>
          <p className="text-sm text-[#ebeef5]/70 mt-1">Auth events and Owner mutations. 90-day rolling retention.</p>
        </div>
        <div className="flex gap-2">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            data-testid="owner-audit-filter"
            className="bg-[#0a1530] border border-white/10 rounded-md px-3 py-2 text-xs text-[#f1fcff] focus:outline-none focus:border-[#10b7c0]/50"
          >
            {EVENT_OPTIONS.map((o) => <option key={o} value={o}>{o || "All events"}</option>)}
          </select>
          <button
            onClick={load}
            className="text-xs font-semibold px-3 py-2 rounded-md border border-white/10 text-[#ebeef5]/70 hover:text-[#f1fcff] flex items-center gap-1.5"
            data-testid="owner-audit-refresh"
          >
            <RefreshCw className="h-3 w-3" /> Refresh
          </button>
        </div>
      </div>

      {events === null ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-[#10b7c0]" /></div>
      ) : (
        <div className="bg-[#0a1530] border border-white/10 rounded-xl overflow-hidden">
          <table className="w-full text-sm" data-testid="owner-audit-table">
            <thead className="bg-white/[0.02] text-[10px] uppercase tracking-wider text-[#ebeef5]/70">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">Event</th>
                <th className="text-left px-5 py-3 font-semibold">Actor</th>
                <th className="text-left px-5 py-3 font-semibold">Target</th>
                <th className="text-left px-5 py-3 font-semibold">Metadata</th>
                <th className="text-left px-5 py-3 font-semibold">IP</th>
                <th className="text-left px-5 py-3 font-semibold">Device</th>
                <th className="text-left px-5 py-3 font-semibold">Time</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {events.length === 0 ? (
                <tr><td colSpan={7} className="px-5 py-12 text-center text-xs text-[#ebeef5]/50">No events.</td></tr>
              ) : events.map((e) => (
                <tr key={e.id} className="hover:bg-white/[0.02]" data-testid={`audit-row-${e.id}`}>
                  <td className="px-5 py-2">
                    <code className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-[#10b7c0]/10 text-[#10b7c0] border border-[#10b7c0]/20 tracking-wider">
                      {e.event_type}
                    </code>
                  </td>
                  <td className="px-5 py-2 text-xs text-[#ebeef5]">{e.actor_email || <span className="text-[#ebeef5]/50">—</span>}</td>
                  <td className="px-5 py-2 text-xs text-[#ebeef5]/70">
                    {e.target_type ? `${e.target_type}:${(e.target_id || "").slice(0, 8)}` : "—"}
                  </td>
                  <td className="px-5 py-2 text-[11px] text-[#ebeef5]/70 font-mono max-w-[260px] truncate" title={JSON.stringify(e.metadata)}>
                    {e.metadata && Object.keys(e.metadata).length ? JSON.stringify(e.metadata) : "—"}
                  </td>
                  <td className="px-5 py-2 text-[11px] text-[#ebeef5]/50 font-mono">{e.ip_address || "—"}</td>
                  <td className="px-5 py-2 text-[11px] text-[#ebeef5]/50 font-mono max-w-[200px] truncate" title={e.user_agent || ""}>
                    {e.user_agent ? shortenUA(e.user_agent) : "—"}
                  </td>
                  <td className="px-5 py-2 text-[11px] text-[#ebeef5]/50">{formatCT(e.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
